import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Loader2, ShieldCheck } from "lucide-react";

export default function Login() {
  const { user, login, verifyCode } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null); // { challenge_id, email }
  const [code, setCode] = useState("");

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email.trim(), password);
      if (res?.two_factor_required) {
        setChallenge({ challenge_id: res.challenge_id, email: res.email });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(apiError(err, "Invalid email or password"));
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyCode(challenge.challenge_id, code.trim());
      navigate("/", { replace: true });
    } catch (err) {
      setError(apiError(err, "Incorrect code"));
    } finally {
      setLoading(false);
    }
  };

  const inp = "h-12 w-full rounded-xl border border-white/10 bg-black px-4 text-white outline-none transition-colors focus:border-[#2997ff]";

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-black px-6">
      <div className="pointer-events-none absolute left-1/2 top-24 h-72 w-72 -translate-x-1/2 rounded-full bg-[#2997ff] opacity-20 blur-[120px]" />
      <div className="mf-fade-in relative w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="text-3xl font-extrabold tracking-tight">
            MacFreaks <span className="text-[#2997ff]">Hub</span>
          </div>
          <p className="mt-2 text-sm text-[#98989d]">Pre-owned MacBooks · Vikhroli, Mumbai</p>
        </div>

        {!challenge ? (
          <form onSubmit={submit} className="space-y-3 rounded-3xl border border-white/10 bg-[#121214] p-6">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-[#98989d]">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inp}
                placeholder="you@macfreaks.in"
                required
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-[#98989d]">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inp}
                placeholder="••••••••"
                required
              />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-[#ff453a]">{error}</p>}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="flex h-12 w-full items-center justify-center rounded-xl bg-[#2997ff] font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Sign in"}
            </button>
          </form>
        ) : (
          <form onSubmit={submitCode} className="space-y-3 rounded-3xl border border-white/10 bg-[#121214] p-6">
            <div className="mb-1 flex items-center gap-2 text-[#2997ff]"><ShieldCheck size={18} /> <span className="text-sm font-semibold">Two-step verification</span></div>
            <p className="text-sm text-[#98989d]">We emailed a 6-digit code to {challenge.email}. Enter it to finish signing in.</p>
            <input
              data-testid="login-2fa-code-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className={`${inp} tracking-[0.4em] text-center text-lg`}
              placeholder="••••••"
              maxLength={6}
              required
            />
            {error && <p data-testid="login-error" className="text-sm text-[#ff453a]">{error}</p>}
            <button
              data-testid="login-2fa-submit-button"
              type="submit"
              disabled={loading}
              className="flex h-12 w-full items-center justify-center rounded-xl bg-[#2997ff] font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Verify & sign in"}
            </button>
            <button type="button" onClick={() => { setChallenge(null); setCode(""); setError(""); }} className="w-full text-center text-sm text-[#98989d]">Back</button>
          </form>
        )}
      </div>
    </div>
  );
}
